/**
 * Address validation and formatting utilities
 */

/**
 * Validates an address object
 * @param {Object} address - The address to validate
 * @returns {Object} { isValid: boolean, errors: {field: string} }
 */
export function validateAddress(address) {
  const errors = {};
  const requiredFields = ["addressLine1", "city", "state", "postalCode", "country", "type"];

  // Check required fields
  requiredFields.forEach((field) => {
    if (!address || !address[field]) {
      errors[field] = `${field} is required`;
    }
  });

  // Validate type if present
  if (address?.type && !["shipping", "billing"].includes(address.type)) {
    errors.type = 'Type must be "shipping" or "billing"';
  }

  const isValid = Object.keys(errors).length === 0;

  return {
    isValid,
    errors,
  };
}

/**
 * Formats an address for full display
 * @param {Object} address - The address to format
 * @returns {string} Formatted address like "123 Main St, Mumbai, MH, 400001, India"
 */
export function formatAddressDisplay(address) {
  if (!address) return "";

  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.postalCode,
    address.country,
  ];

  return parts.filter((part) => part !== null && part !== undefined).join(", ");
}

/**
 * Formats an address for preview display (full address)
 * @param {Object} address - The address to format
 * @returns {string} Preview like "123 Main St, Mumbai, MH, 400001, India"
 */
export function formatAddressPreview(address) {
  if (!address) return "";

  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.state,
    address.postalCode,
    address.country,
  ];

  return parts.filter((part) => part !== null && part !== undefined).join(", ");
}

/**
 * Gets the display label for an address type
 * @param {string} type - The address type ("shipping" or "billing")
 * @returns {string} The display label
 */
export function getAddressTypeLabel(type) {
  const labels = {
    shipping: "Shipping",
    billing: "Billing",
  };

  return labels[type] || "";
}
